import {
  getCookie,
  setCookie,
  updateCookie,
  deleteCookie,
} from '@/lib/utils';
import store from '@/store';
/**
 * Centralized hook for managing loft-workstation cookie state via underlying cookie CRUD utilities.
 *
 * NOTE: The save_site_cashdrawer_on_login system parameter does not apply as workstation data from cookie needs
 *       to hydrate state in the event of a valid JWT token since workstation data is not included in IndexedDB cache.
 * ENHANCEMENT: Detecting whether browser does not have cookies enabled.
 *
 * @returns {Object.hasRequiredWorkstationData} - Checks to see whether workstation data has required siteid, site, and cashdrawer properties.
 * @returns {Object.getWorkstationCookie} - Gets lof-workstation cookie data.
 * @returns {Object.setWorkstationCookie} - Sets workstation cookie to 365 days with required siteid, site, and cashdrawer properties (otherwise throws error).
 * @returns {Object.updateWorkstationCookie} - Updates workstation cookie DURATION to 365 days.
 * @returns {Object.deleteWorkstationCookie} - Deletes loft-workstation cookie.
 */
export default function useWorkstationCookie() {
  const COOKIENAME = 'loft-workstation';
  const DURATION = 365;

  const encode = payload => btoa(JSON.stringify(payload));
  const decode = payload => JSON.parse(atob(payload));
  const hasRequiredWorkstationData = ({ siteid, cashdrawerid }) => siteid && cashdrawerid;
  const getWorkstationCookie = () => {
    const cookie = getCookie(COOKIENAME);
    return cookie ? decode(cookie) : null;
  };
  const setWorkstationCookie = (workstationData) => {
    if (!store.getters['auth/shouldSaveSiteAndDrawer']) return;
    const { siteid, cashdrawerid } = workstationData;

    if (!hasRequiredWorkstationData(workstationData)) {
      throw new Error('Invalid workstation data', workstationData);
    }

    setCookie(COOKIENAME, encode({ siteid, cashdrawerid }), DURATION);
  };
  const updateWorkstationCookie = (workstationData) => {
    if (!store.getters['auth/shouldSaveSiteAndDrawer']) return;
    const { siteid, cashdrawerid } = workstationData;
    updateCookie(COOKIENAME, encode({ siteid, cashdrawerid }), DURATION);
  };
  const deleteWorkstationCookie = () => {
    deleteCookie(COOKIENAME);
  };

  return {
    hasRequiredWorkstationData,
    getWorkstationCookie,
    setWorkstationCookie,
    updateWorkstationCookie,
    deleteWorkstationCookie,
  };
}
